import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const POLL_INTERVAL_MS = 30000;

// Single source of truth for GET /api/sync/background-status, polled once
// for the whole app instead of once per component (SyncStatusBanner,
// WelcomeVideoOverlay, ...) that needs to know sync progress.
const SyncStatusContext = createContext(null);

export const SyncStatusProvider = ({ children }) => {
  const { authToken } = useAuth();
  const [syncStatus, setSyncStatus] = useState(null);
  const intervalRef = useRef(null);

  const checkStatus = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/sync/background-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setSyncStatus(data);

      // Stop polling once there's nothing left to move
      const bgActive = ['queued', 'running'].includes(data.background_status);
      const criticalActive = data.critical_sync_status === 'paused_budget' && !data.critical_completed;
      if (!bgActive && !criticalActive && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return data;
    } catch (err) {
      setSyncStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!authToken) {
      setSyncStatus(null);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    checkStatus(authToken);
    intervalRef.current = setInterval(() => checkStatus(authToken), POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [authToken, checkStatus]);

  // Lets a caller (e.g. after triggering a manual resync) force an immediate
  // re-check and resume polling if it had stopped.
  const refresh = useCallback(() => {
    if (!authToken) return;
    checkStatus(authToken);
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => checkStatus(authToken), POLL_INTERVAL_MS);
    }
  }, [authToken, checkStatus]);

  return (
    <SyncStatusContext.Provider value={{ syncStatus, refresh }}>
      {children}
    </SyncStatusContext.Provider>
  );
};

export const useSyncStatus = () => {
  const ctx = useContext(SyncStatusContext);
  if (!ctx) {
    throw new Error('useSyncStatus must be used within a SyncStatusProvider');
  }
  return ctx;
};
